import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { startOfDay, startOfMonth } from 'date-fns';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../utils/axiosConfig';
import { clientIanaTimeZone } from '../../utils/clientTimeZone';
import { cn } from '../../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import RunningExerciseCard from '../training/RunningExerciseCard';
import RunningExerciseTemplatePickerModal from '../training/RunningExerciseTemplatePickerModal';
import UserRunningExerciseEditorModal from '../training/UserRunningExerciseEditorModal';
import ActivityPeekModal from '../ActivityPeekModal';
import DayCombinedSummaryModal from './DayCombinedSummaryModal';
import PlannerLibraryPanel from './PlannerLibraryPanel';
import PlannerMonthGrid from './PlannerMonthGrid';
import {
  addMonths,
  defaultHourForNthPlanned,
  formatDayKey,
  LINKED_STRAVA_ACTIVITY_META_KEY,
  localOccurrenceAt,
  monthGridBounds,
  normalizePlannerItem,
  parseItemMetadata,
  shiftDateKeepTime,
} from './plannerUtils';

export default function TrainingPlannerModal({ open, onOpenChange, onPlannedChanged }) {
  const { t } = useLanguage();
  const [visibleMonth, setVisibleMonth] = useState(() => startOfMonth(new Date()));
  const [libraryScope, setLibraryScope] = useState('global');
  const [plannedItems, setPlannedItems] = useState([]);
  const [loadingPlanned, setLoadingPlanned] = useState(false);
  const [activeDrag, setActiveDrag] = useState(null);

  const [activityByDay, setActivityByDay] = useState({});
  const [acwrByDay, setAcwrByDay] = useState(() => new Map());

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const [peekActivityId, setPeekActivityId] = useState(null);
  const [combinedDayKey, setCombinedDayKey] = useState(null);

  const [runEditorOpen, setRunEditorOpen] = useState(false);
  const [runEditorExerciseId, setRunEditorExerciseId] = useState(null);
  const [runEditorLogItem, setRunEditorLogItem] = useState(null);
  const [plannerCreateTemplateToolbar, setPlannerCreateTemplateToolbar] = useState(false);
  const [addWorkoutChoiceOpen, setAddWorkoutChoiceOpen] = useState(false);
  const [addWorkoutDayKey, setAddWorkoutDayKey] = useState(null);
  const [templatePickerForCreateOpen, setTemplatePickerForCreateOpen] = useState(false);
  const plannerCreateCleanupLogIdRef = useRef(null);
  const plannerCreateSavedRef = useRef(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const { gridStart, gridEnd } = useMemo(() => monthGridBounds(visibleMonth), [visibleMonth]);
  const fromYmd = formatDayKey(gridStart);
  const toYmd = formatDayKey(gridEnd);

  const loadPlanned = useCallback(async (opts = {}) => {
    const silent = opts.silent === true;
    if (!silent) setLoadingPlanned(true);
    try {
      const res = await api.get('/api/training-log/items', {
        params: {
          from: fromYmd,
          to: toYmd,
          typeKey: 'planned_session',
          limit: 500,
        },
      });
      const raw = res.data?.items || [];
      setPlannedItems(raw.map(normalizePlannerItem));
    } catch (e) {
      console.error(e);
      if (!silent) setPlannedItems([]);
    } finally {
      if (!silent) setLoadingPlanned(false);
    }
  }, [fromYmd, toYmd]);

  const loadActivitySummary = useCallback(async () => {
    try {
      const res = await api.get('/api/training-log/planner-activity-summary', {
        params: { from: fromYmd, to: toYmd },
      });
      setActivityByDay(res.data?.byDay || {});
    } catch (e) {
      console.error(e);
      setActivityByDay({});
    }
  }, [fromYmd, toYmd]);

  const loadAcwr = useCallback(async () => {
    try {
      const res = await api.get('/api/training-log/timeline-acwr', {
        params: {
          from: fromYmd,
          to: toYmd,
          today: formatDayKey(startOfDay(new Date())),
          tz: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        },
      });
      const entries = res.data?.entries || [];
      const m = new Map();
      for (const e of entries) {
        if (e?.date) m.set(e.date, e.acwr ?? null);
      }
      setAcwrByDay(m);
    } catch (e) {
      console.error(e);
      setAcwrByDay(new Map());
    }
  }, [fromYmd, toYmd]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    loadAcwr();
    (async () => {
      await loadActivitySummary();
      if (cancelled) return;
      await loadPlanned();
    })();
    return () => {
      cancelled = true;
    };
  }, [open, loadPlanned, loadActivitySummary, loadAcwr]);

  useEffect(() => {
    if (!open) return undefined;
    const refresh = () => {
      void loadActivitySummary();
      void loadPlanned();
      void loadAcwr();
    };
    window.addEventListener('trainingUpdated', refresh);
    return () => window.removeEventListener('trainingUpdated', refresh);
  }, [open, loadPlanned, loadActivitySummary, loadAcwr]);

  const plannedByDay = useMemo(() => {
    const map = {};
    for (const item of plannedItems) {
      if (!item.occurred_at) continue;
      const key = formatDayKey(new Date(item.occurred_at));
      if (!map[key]) map[key] = [];
      map[key].push(item);
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => new Date(a.occurred_at) - new Date(b.occurred_at));
    }
    return map;
  }, [plannedItems]);

  const notifyParent = useCallback(() => {
    onPlannedChanged?.();
  }, [onPlannedChanged]);

  const stripLinkMetadata = useCallback((item) => {
    const meta = parseItemMetadata(item.metadata) || {};
    const next = { ...meta };
    delete next[LINKED_STRAVA_ACTIVITY_META_KEY];
    return next;
  }, []);

  const handleDragStart = (event) => {
    setActiveDrag(event.active);
  };

  const snapshotPlannedItems = (items) =>
    items.map((i) => ({
      ...i,
      metadata:
        i.metadata && typeof i.metadata === 'object' ? { ...i.metadata } : i.metadata,
    }));

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveDrag(null);
    if (!over) return;
    const overId = String(over.id);
    if (!overId.startsWith('day-')) return;
    const dayKey = overId.slice(4);
    const activeId = String(active.id);

    if (activeId.startsWith('library-')) {
      const data = active.data.current || {};
      let sessionId =
        data.trainingSessionId != null && Number.isFinite(Number(data.trainingSessionId))
          ? Number(data.trainingSessionId)
          : null;
      if (!sessionId && data.needsPlannerSession && data.trainingExerciseId) {
        try {
          const sessRes = await api.post(`/api/training/exercises/${data.trainingExerciseId}/planner-session`);
          sessionId = sessRes.data?.trainingSessionId ?? null;
        } catch (e) {
          console.error(e);
          return;
        }
      }
      if (!sessionId) return;
      const n = plannedByDay[dayKey]?.length ?? 0;
      const hour = defaultHourForNthPlanned(n, dayKey);
      const occurredAt = localOccurrenceAt(dayKey, hour, 0).toISOString();
      const tempId = -Math.abs(Date.now());
      const optimistic = normalizePlannerItem({
        id: tempId,
        occurred_at: occurredAt,
        type_key: 'planned_session',
        title: data.title || null,
        summary: data.description || null,
        training_session: {
          id: sessionId,
          name: data.title,
          description: data.description,
          preview_splits: data.preview_splits || [],
        },
        user_running_exercise: null,
        user_running_exercise_id: null,
      });
      setPlannedItems((prev) => [...prev, optimistic]);
      try {
        const postRes = await api.post('/api/training-log/items', {
          typeKey: 'planned_session',
          trainingSessionId: sessionId,
          occurredAt,
          title: data.title || null,
          summary: data.description || null,
        });
        const newItem = postRes.data?.item;
        const newId = newItem?.id;
        if (newId) {
          let merged = normalizePlannerItem(newItem);
          const existingPlanned = plannedByDay[dayKey] || [];
          const dayAlreadyHasLinkedPlanned = existingPlanned.some((p) => {
            const v = Number(p?.metadata?.[LINKED_STRAVA_ACTIVITY_META_KEY]);
            return Number.isFinite(v) && v > 0;
          });
          if (!dayAlreadyHasLinkedPlanned) {
            const sumRes = await api.get('/api/training-log/planner-activity-summary', {
              params: { from: fromYmd, to: toYmd },
            });
            const daySum = sumRes.data?.byDay?.[dayKey];
            if (daySum?.stravaActivityIds?.length === 1) {
              const baseMeta = parseItemMetadata(merged.metadata) || {};
              await api.patch(`/api/training-log/items/${newId}`, {
                metadata: {
                  ...baseMeta,
                  [LINKED_STRAVA_ACTIVITY_META_KEY]: daySum.stravaActivityIds[0],
                },
              });
              merged = {
                ...merged,
                metadata: {
                  ...baseMeta,
                  [LINKED_STRAVA_ACTIVITY_META_KEY]: daySum.stravaActivityIds[0],
                },
              };
            }
          }
          setPlannedItems((prev) => prev.map((i) => (i.id === tempId ? merged : i)));
        } else {
          setPlannedItems((prev) => prev.filter((i) => i.id !== tempId));
        }
        void loadPlanned({ silent: true });
        void loadActivitySummary();
        notifyParent();
      } catch (e) {
        console.error(e);
        setPlannedItems((prev) => prev.filter((i) => i.id !== tempId));
      }
      return;
    }

    if (activeId.startsWith('planned-')) {
      const itemId = parseInt(activeId.replace('planned-', ''), 10);
      if (!itemId) return;
      const item = plannedItems.find((i) => i.id === itemId);
      if (!item) return;
      const currentDay = formatDayKey(new Date(item.occurred_at));
      if (currentDay === dayKey) return;
      const next = shiftDateKeepTime(item.occurred_at, dayKey);
      const nextIso = next.toISOString();
      const nextMeta = stripLinkMetadata(item);
      let before = null;
      setPlannedItems((prev) => {
        before = snapshotPlannedItems(prev);
        return prev.map((i) =>
          i.id === itemId ? { ...i, occurred_at: nextIso, metadata: nextMeta } : i
        );
      });
      try {
        await api.patch(`/api/training-log/items/${itemId}`, {
          occurredAt: nextIso,
          metadata: nextMeta,
          timeZone: clientIanaTimeZone(),
        });
        void loadPlanned({ silent: true });
        notifyParent();
      } catch (e) {
        console.error(e);
        if (before) setPlannedItems(before);
      }
    }
  };

  const handleTimeChange = async (itemId, occurredAtIso) => {
    let before = null;
    setPlannedItems((prev) => {
      before = snapshotPlannedItems(prev);
      return prev.map((i) => (i.id === itemId ? { ...i, occurred_at: occurredAtIso } : i));
    });
    try {
      await api.patch(`/api/training-log/items/${itemId}`, {
        occurredAt: occurredAtIso,
        timeZone: clientIanaTimeZone(),
      });
      void loadPlanned({ silent: true });
      notifyParent();
    } catch (e) {
      console.error(e);
      if (before) setPlannedItems(before);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/api/training-log/items/${deleteTarget.id}`);
      setDeleteTarget(null);
      await loadPlanned();
      notifyParent();
    } catch (e) {
      console.error(e);
    } finally {
      setDeleteBusy(false);
    }
  };

  const handleEditStructure = (item) => {
    if (!item.user_running_exercise_id) return;
    plannerCreateCleanupLogIdRef.current = null;
    setPlannerCreateTemplateToolbar(false);
    setRunEditorExerciseId(item.user_running_exercise_id);
    setRunEditorLogItem(item);
    setRunEditorOpen(true);
  };

  const handleEditActivityExercise = (ureId) => {
    if (!ureId) return;
    plannerCreateCleanupLogIdRef.current = null;
    setPlannerCreateTemplateToolbar(false);
    setRunEditorExerciseId(ureId);
    setRunEditorLogItem(null);
    setRunEditorOpen(true);
  };

  const buildOccurredAtForDay = useCallback(
    (dayKey) => {
      const n = plannedByDay[dayKey]?.length ?? 0;
      const hour = defaultHourForNthPlanned(n, dayKey);
      return localOccurrenceAt(dayKey, hour, 0).toISOString();
    },
    [plannedByDay]
  );

  const finishPlannerCreateFromDay = useCallback(
    async (dayKey, templateTrainingExerciseId) => {
      const occurredAt = buildOccurredAtForDay(dayKey);
      const draftRes = await api.post('/api/user-running-exercises/planner-draft', {
        occurredAt,
        ...(templateTrainingExerciseId ? { templateTrainingExerciseId } : {}),
      });
      const exerciseId = draftRes.data?.exerciseId;
      if (!exerciseId) throw new Error('no_exercise');
      const logRes = await api.post('/api/training-log/items', {
        typeKey: 'planned_session',
        occurredAt,
        userRunningExerciseId: exerciseId,
        timeZone: clientIanaTimeZone(),
      });
      const item = normalizePlannerItem(logRes.data?.item);
      plannerCreateCleanupLogIdRef.current = item?.id ?? null;
      plannerCreateSavedRef.current = false;
      setPlannerCreateTemplateToolbar(true);
      setRunEditorExerciseId(exerciseId);
      setRunEditorLogItem(item);
      setRunEditorOpen(true);
      await loadPlanned({ silent: true });
      notifyParent();
    },
    [buildOccurredAtForDay, loadPlanned, notifyParent]
  );

  const openAddWorkoutForDay = (dayKey) => {
    setAddWorkoutDayKey(dayKey);
    setAddWorkoutChoiceOpen(true);
  };

  const handleAddWorkoutBlank = async () => {
    if (!addWorkoutDayKey) return;
    const dayKey = addWorkoutDayKey;
    setAddWorkoutChoiceOpen(false);
    try {
      await finishPlannerCreateFromDay(dayKey, null);
    } catch (e) {
      console.error(e);
    }
  };

  const handleAddWorkoutPickTemplate = () => {
    setAddWorkoutChoiceOpen(false);
    setTemplatePickerForCreateOpen(true);
  };

  const handleTemplatePickedForPlannerCreate = async (templateExerciseId) => {
    setTemplatePickerForCreateOpen(false);
    if (!addWorkoutDayKey || !templateExerciseId) return;
    try {
      await finishPlannerCreateFromDay(addWorkoutDayKey, templateExerciseId);
    } catch (e) {
      console.error(e);
    }
  };

  const overlayPreview = useMemo(() => {
    if (!activeDrag) return null;
    const id = String(activeDrag.id);
    const data = activeDrag.data?.current;
    if (id.startsWith('library-') && data) {
      return (
        <RunningExerciseCard
          title={data.title}
          description={data.description}
          splits={data.preview_splits}
          vizHeightPx={28}
          className="max-w-[220px] shadow-lg"
        />
      );
    }
    if (id.startsWith('planned-') && data?.item) {
      const it = data.item;
      const ure = it.user_running_exercise;
      const splits =
        ure?.preview_splits?.length ? ure.preview_splits : it.training_session?.preview_splits || [];
      return (
        <RunningExerciseCard
          title={ure?.title || it.title}
          description={ure?.description || it.summary}
          splits={splits}
          vizHeightPx={20}
          className="max-w-[200px] shadow-lg"
        />
      );
    }
    return null;
  }, [activeDrag]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          priority="high"
          showCloseButton
          className={cn(
            '!left-[2.5vw] !top-[2.5vh] !max-h-[95vh] !h-[95vh] !w-[95vw] !max-w-[95vw] !translate-x-0 !translate-y-0',
            'flex flex-col gap-0 overflow-hidden p-0 sm:rounded-lg'
          )}
        >
          <DialogHeader className="shrink-0 space-y-0 border-b border-border px-4 py-3 pr-12 text-left">
            <DialogTitle className="text-base">{t('trainingLog.plannerTitle')}</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              {t('trainingLog.plannerDescription')}
            </DialogDescription>
          </DialogHeader>
          <DndContext
            sensors={sensors}
            collisionDetection={pointerWithin}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div className="relative flex min-h-0 flex-1 overflow-hidden">
              <PlannerLibraryPanel scope={libraryScope} onScopeChange={setLibraryScope} />
              <div className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                {loadingPlanned && (
                  <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-background/30" />
                )}
                <PlannerMonthGrid
                  visibleMonth={visibleMonth}
                  onPrevMonth={() => setVisibleMonth((m) => addMonths(m, -1))}
                  onNextMonth={() => setVisibleMonth((m) => addMonths(m, 1))}
                  plannedByDay={plannedByDay}
                  activityByDay={activityByDay}
                  acwrByDay={acwrByDay}
                  onTimeChange={handleTimeChange}
                  onEditStructure={handleEditStructure}
                  onEditActivityExercise={handleEditActivityExercise}
                  onRequestDelete={setDeleteTarget}
                  onActivityPeek={setPeekActivityId}
                  onDayCombinedSummary={setCombinedDayKey}
                  onOpenLinkedActivity={setPeekActivityId}
                  onRequestAddExercise={openAddWorkoutForDay}
                />
              </div>
            </div>
            <DragOverlay dropAnimation={null} style={{ zIndex: deleteTarget ? 0 : 10060 }}>
              {overlayPreview}
            </DragOverlay>
          </DndContext>

          <Dialog
            open={!!deleteTarget}
            onOpenChange={(nextOpen) => {
              if (!nextOpen && !deleteBusy) setDeleteTarget(null);
            }}
          >
            <DialogContent
              priority="nested"
              showCloseButton={false}
              className="max-w-[min(92vw,24rem)] gap-4 p-4 sm:p-6"
              onPointerDownOutside={(e) => {
                if (deleteBusy) e.preventDefault();
              }}
              onEscapeKeyDown={(e) => {
                if (deleteBusy) e.preventDefault();
              }}
            >
              <DialogHeader className="space-y-2 text-left">
                <DialogTitle className="text-sm font-semibold">{t('trainingLog.plannerDeleteTitle')}</DialogTitle>
                <DialogDescription className="text-xs">{t('trainingLog.plannerDeleteConfirm')}</DialogDescription>
              </DialogHeader>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={deleteBusy}
                  onClick={() => setDeleteTarget(null)}
                >
                  {t('common.cancel')}
                </Button>
                <Button type="button" variant="destructive" size="sm" disabled={deleteBusy} onClick={confirmDelete}>
                  {t('common.delete')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </DialogContent>
      </Dialog>

      <ActivityPeekModal activityId={peekActivityId} onClose={() => setPeekActivityId(null)} />

      <DayCombinedSummaryModal
        open={!!combinedDayKey}
        onClose={() => setCombinedDayKey(null)}
        dayKey={combinedDayKey}
        dayData={combinedDayKey ? activityByDay[combinedDayKey] : null}
        onActivityPeek={(id) => { setCombinedDayKey(null); setPeekActivityId(id); }}
      />

      <Dialog open={addWorkoutChoiceOpen} onOpenChange={setAddWorkoutChoiceOpen}>
        <DialogContent priority="nested" className="max-w-[min(92vw,24rem)] gap-4 p-4 sm:p-6">
          <DialogHeader className="space-y-2 text-left">
            <DialogTitle className="text-sm font-semibold">{t('trainingLog.plannerAddWorkoutTitle')}</DialogTitle>
            <DialogDescription className="text-xs">{t('trainingLog.plannerAddWorkoutDescription')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" size="sm" onClick={() => setAddWorkoutChoiceOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={handleAddWorkoutPickTemplate}>
              {t('trainingLog.plannerAddWorkoutFromTemplate')}
            </Button>
            <Button type="button" size="sm" onClick={handleAddWorkoutBlank}>
              {t('trainingLog.plannerAddWorkoutBlank')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <RunningExerciseTemplatePickerModal
        open={templatePickerForCreateOpen}
        onOpenChange={setTemplatePickerForCreateOpen}
        onPickTemplate={handleTemplatePickedForPlannerCreate}
      />

      <UserRunningExerciseEditorModal
        open={runEditorOpen}
        onOpenChange={(o) => {
          setRunEditorOpen(o);
          if (!o) {
            const lid = plannerCreateCleanupLogIdRef.current;
            if (lid && !plannerCreateSavedRef.current) {
              api.delete(`/api/training-log/items/${lid}`).catch(() => {});
            }
            plannerCreateCleanupLogIdRef.current = null;
            plannerCreateSavedRef.current = false;
            setPlannerCreateTemplateToolbar(false);
            setRunEditorExerciseId(null);
            setRunEditorLogItem(null);
          }
        }}
        exerciseId={runEditorExerciseId}
        trainingLogItemId={runEditorLogItem?.id ?? null}
        diaryOccurredAt={runEditorLogItem?.occurred_at ?? null}
        diaryMetadata={runEditorLogItem?.metadata ?? null}
        showTemplateToolbarForInstance={plannerCreateTemplateToolbar}
        onSaved={async () => {
          plannerCreateSavedRef.current = true;
          plannerCreateCleanupLogIdRef.current = null;
          setPlannerCreateTemplateToolbar(false);
          await loadPlanned();
          notifyParent();
        }}
      />
    </>
  );
}
